import React, { useState, useEffect } from 'react'
import { useAppSelector } from '../../../../store/hooks'
import {
  selectSelectedProjectId,
  setShowingNewAAL,
  selectShowingNewAAL,
} from '../../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../../services/projectsApi'
import {
  modelsApi,
  useGetModelQuery,
  useRunBatchInferenceMutation,
  useStopBatchInferenceMutation,
} from '../../../../services/modelsApi'
import { selectSelectedModelId, setSelectedModelId } from '../../../../store/slices/modelSlice'
import styled from 'styled-components'
import {
  FaSpinner,
  FaCheck,
  FaExclamationTriangle,
  FaStop,
  FaImages,
  FaDatabase,
  FaRocket,
  FaChartBar,
  FaCheckCircle,
  FaExclamationCircle,
  FaPlus,
  FaTimes,
  FaCalendarAlt,
  FaRedo,
  FaEdit,
} from 'react-icons/fa'
import ErrorDialog from '../../../modals/ErrorDialog'
import { selectDatasetSources } from '../../../../store/slices/datasetSlice'
import SelectDatasetModal from '../../../modals/SelectDatasetModal'
import SelectModelTreeModal from '../../../modals/SelectModelTreeModal/SelectModelTreeModal'
import { useAppDispatch, useAnnotationStats } from '../../../../store/hooks'
import { apiTags } from '../../../../services/apiTags'
import { isEqual } from 'lodash'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
`

const StatusBar = styled.div`
  background: transparent;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  border: 1px solid rgba(147, 51, 234, 0.2);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-shrink: 0;
  flex-wrap: wrap;
  position: relative;
`

const StatusLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const SelectModelButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  background: rgba(147, 51, 234, 0.1);
  border: 1px solid rgba(147, 51, 234, 0.3);
  color: #9333ea;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(147, 51, 234, 0.2);
    border-color: rgba(147, 51, 234, 0.5);
    color: #c084fc;
  }
`

const ModelBadge = styled.div<{ $deployed: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  background: ${(props) =>
    props.$deployed ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255, 193, 7, 0.1)'};
  border: 1px solid
    ${(props) => (props.$deployed ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 193, 7, 0.3)')};
  color: ${(props) => (props.$deployed ? '#10b981' : '#ffc107')};
  font-size: 0.8rem;
  font-weight: 500;
`

const StatusIcon = styled.div<{ $deployed: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(props) => (props.$deployed ? '#10b981' : '#ffc107')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 0.6rem;
`

const ModelName = styled.span`
  color: #ffffff;
  font-weight: 600;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const QuickStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  color: #b0b0b0;
  font-size: 0.85rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  .value {
    color: #9333ea;
    font-weight: 600;
  }
`

const MainContent = styled.div`
  display: flex;
  gap: 1rem;
  flex: 1;
  min-height: 0;
  align-items: stretch;
  overflow: auto;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-bottom: 1.25rem;
  align-items: stretch;
`

const RightPanel = styled.div`
  flex: 0 0 380px;
  margin-bottom: 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  @media (max-width: 1024px) {
    flex: none;
  }
`

const Card = styled.div`
  background: transparent;
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid rgba(147, 51, 234, 0.1);
  transition: all 0.2s ease;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: rgba(147, 51, 234, 0.2);
  }
`

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 200px;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`

const CardTitle = styled.h3`
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const CardIcon = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 3px;
  background: linear-gradient(135deg, #9333ea 0%, #c084fc 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 0.65rem;
`

const EmptyDatasets = styled.div`
  color: #8c8c8c;
  font-size: 0.8rem;
  font-style: italic;
  padding: 1rem;
  text-align: center;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 6px;
`

const ActionButton = styled.button`
  width: 100%;
  padding: 0.6rem 1rem;
  background: rgba(147, 51, 234, 0.1);
  border: 1px solid rgba(147, 51, 234, 0.3);
  border-radius: 6px;
  color: #9333ea;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;

  &:hover {
    background: rgba(147, 51, 234, 0.2);
    border-color: rgba(147, 51, 234, 0.5);
    color: #c084fc;
  }
`

const LaunchCard = styled.div`
  background: transparent;
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid rgba(147, 51, 234, 0.3);
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #9333ea 0%, #c084fc 100%);
  }
`

const LaunchHeader = styled.div`
  text-align: center;
  margin-bottom: 1.25rem;
`

const LaunchTitle = styled.h2`
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.4rem 0;
`

const LaunchSubtitle = styled.p`
  color: #b0b0b0;
  font-size: 0.8rem;
  margin: 0;
  line-height: 1.4;
`

const ReadinessChecks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
`

const CheckItem = styled.div<{ $ready: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem;
  background: ${(props) => (props.$ready ? 'rgba(16, 185, 129, 0.08)' : 'rgba(220, 53, 69, 0.08)')};
  border: 1px solid
    ${(props) => (props.$ready ? 'rgba(16, 185, 129, 0.2)' : 'rgba(220, 53, 69, 0.2)')};
  border-radius: 6px;
  color: ${(props) => (props.$ready ? '#10b981' : '#dc3545')};
  font-size: 0.75rem;
  font-weight: 500;
`

const CheckIcon = styled.div<{ $ready: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(props) => (props.$ready ? '#10b981' : '#dc3545')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 0.6rem;
  flex-shrink: 0;
`

const LaunchButton = styled.button<{
  $isRunning?: boolean
  $canStart?: boolean
}>`
  width: 100%;
  padding: 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  ${(props) => {
    if (!props.$canStart) {
      return `
        background: rgba(255, 255, 255, 0.1);
        color: #8c8c8c;
        cursor: not-allowed;
      `
    }

    if (props.$isRunning) {
      return `
        background: linear-gradient(135deg, #dc3545 0%, #bb2d3b 100%);
        color: #ffffff;
        box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
      `
    }

    return `
      background: linear-gradient(135deg, #9333ea 0%, #c084fc 100%);
      color: #ffffff;
      box-shadow: 0 4px 12px rgba(147, 51, 234, 0.3);
    `
  }}

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${(props) => {
      if (props.$isRunning) return '0 6px 16px rgba(220, 53, 69, 0.4)'
      return '0 6px 16px rgba(147, 51, 234, 0.4)'
    }};

    &::after {
      opacity: 1;
    }
  }

  svg {
    font-size: 1rem;
  }
`

const StatusContainer = styled.div<{ $status: string }>`
  margin-top: 1rem;
  padding: 0.8rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  font-size: 0.8rem;
  font-weight: 500;
  background: ${(props) => {
    switch (props.$status) {
      case 'RUNNING':
        return 'rgba(147, 51, 234, 0.1)'
      case 'COMPLETE':
        return 'rgba(16, 185, 129, 0.1)'
      case 'COMPLETE_WITH_ERR':
        return 'rgba(255, 193, 7, 0.1)'
      case 'ERR':
        return 'rgba(220, 53, 69, 0.1)'
      default:
        return 'rgba(108, 117, 125, 0.1)'
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$status) {
        case 'RUNNING':
          return 'rgba(147, 51, 234, 0.3)'
        case 'COMPLETE':
          return 'rgba(16, 185, 129, 0.3)'
        case 'COMPLETE_WITH_ERR':
          return 'rgba(255, 193, 7, 0.3)'
        case 'ERR':
          return 'rgba(220, 53, 69, 0.3)'
        default:
          return 'rgba(108, 117, 125, 0.3)'
      }
    }};
  color: ${(props) => {
    switch (props.$status) {
      case 'RUNNING':
        return '#9333ea'
      case 'COMPLETE':
        return '#10b981'
      case 'COMPLETE_WITH_ERR':
        return '#ffc107'
      case 'ERR':
        return '#dc3545'
      default:
        return '#8c8c8c'
    }
  }};

  .spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

const ProgressBar = styled.div<{ $progress: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: ${(props) => props.$progress}%;
  background: linear-gradient(90deg, #9333ea 0%, #10b981 100%);
  transition: width 0.5s ease;
  border-radius: 0 0 12px 12px;
`

const DatasetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 250px));
  gap: 0.75rem;
  width: 100%;
  justify-content: start;
`

const DatasetCard = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  height: 100%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    border-color: rgba(102, 126, 234, 0.4);
  }

  .selected-indicator {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
    z-index: 2;
    font-size: 0.7rem;
  }

  .dataset-icon {
    position: absolute;
    top: 8px;
    left: 8px;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
    font-size: 0.8rem;
  }
`

const DatasetCardHeader = styled.div`
  background: linear-gradient(145deg, #1a202c 0%, #2d3748 100%);
  padding: 2.25rem 0.75rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;

  .dataset-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: #ffffff;
    margin: 0 0 0.25rem 2.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .dataset-source {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.7);
    margin-left: 2.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const DatasetCardBody = styled.div`
  padding: 0.75rem;
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
`

const DatasetStatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`

const DatasetStatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .stat-icon {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: linear-gradient(135deg, #667eea30 0%, #764ba230 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #667eea;
    font-size: 0.7rem;
    flex-shrink: 0;
  }

  .stat-content {
    min-width: 0;
  }

  .stat-label {
    font-size: 0.65rem;
    color: #b0b0b0;
    font-weight: 500;
    margin-bottom: 0.1rem;
  }

  .stat-value {
    font-size: 0.8rem;
    font-weight: 600;
    color: #ffffff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const DatasetTypeBadge = styled.div<{ $type: string }>`
  position: absolute;
  top: 0.5rem;
  right: 2rem;
  background: ${(props) =>
    props.$type === 'training'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%)'};
  color: white;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`

const NewAALButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, #9333ea 0%, #c084fc 100%);
  border: 1px solid #9333ea;
  border-radius: 6px;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #7c2d12 0%, #a855f7 100%);
    border-color: #7c2d12;
    transform: translateY(-1px);
  }

  svg {
    font-size: 0.7rem;
  }
`

const BatchResultsCard = styled.div`
  background: transparent;
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid rgba(147, 51, 234, 0.1);
  transition: all 0.2s ease;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: rgba(147, 51, 234, 0.2);
  }
`

const BatchResultsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`

const BatchResultsTitle = styled.h3`
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const BatchStatusBadge = styled.div<{ $status: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  background: ${(props: { $status: string }) => {
    switch (props.$status) {
      case 'COMPLETE':
        return 'rgba(16, 185, 129, 0.1)'
      case 'COMPLETE_WITH_ERR':
        return 'rgba(255, 193, 7, 0.1)'
      case 'ERR':
        return 'rgba(220, 53, 69, 0.1)'
      default:
        return 'rgba(108, 117, 125, 0.1)'
    }
  }};
  border: 1px solid
    ${(props: { $status: string }) => {
      switch (props.$status) {
        case 'COMPLETE':
          return 'rgba(16, 185, 129, 0.3)'
        case 'COMPLETE_WITH_ERR':
          return 'rgba(255, 193, 7, 0.3)'
        case 'ERR':
          return 'rgba(220, 53, 69, 0.3)'
        default:
          return 'rgba(108, 117, 125, 0.3)'
      }
    }};
  color: ${(props: { $status: string }) => {
    switch (props.$status) {
      case 'COMPLETE':
        return '#10b981'
      case 'COMPLETE_WITH_ERR':
        return '#ffc107'
      case 'ERR':
        return '#dc3545'
      default:
        return '#8c8c8c'
    }
  }};
  font-size: 0.8rem;
  font-weight: 500;
`

const BatchStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(125px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`

const BatchStatCard = styled.div`
  background: #333333;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid rgba(147, 51, 234, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const BatchStatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #9333ea 0%, #c084fc 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 1rem;
  margin-bottom: 0.75rem;
`

const BatchStatValue = styled.div`
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`

const BatchStatLabel = styled.div`
  color: #b0b0b0;
  font-size: 0.8rem;
  font-weight: 500;
`

const BatchMetadata = styled.div`
  background: #333333;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid rgba(147, 51, 234, 0.1);
`

const BatchMetadataTitle = styled.h4`
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const BatchDatasetTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`

const BatchDatasetTag = styled.span`
  background: rgba(102, 126, 234, 0.15);
  color: #667eea;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(102, 126, 234, 0.3);
`

const BatchTimeline = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
`

const BatchTimelineItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const BatchTimelineLabel = styled.div`
  color: #b0b0b0;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const BatchTimelineValue = styled.div`
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 500;
`

const RunPhase: React.FC = () => {
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showSelectModal, setShowSelectModal] = useState(false)
  const [showSelectModelModal, setShowSelectModelModal] = useState(false)
  const [localRunningState, setLocalRunningState] = useState(false)
  const [aalDatasetSources, setAalDatasetSources] = useState<string[]>([])
  const [hasUnlabeledImages, setHasUnlabeledImages] = useState(false)

  const dispatch = useAppDispatch()

  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  // const datasetSources = useAppSelector(selectDatasetSources)
  const showingNewAAL = useAppSelector(selectShowingNewAAL)

  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const { data: model } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
    pollingInterval: 60000,
  })

  const [runBatchInference, { isLoading }] = useRunBatchInferenceMutation()
  const [stopBatchInference] = useStopBatchInferenceMutation()
  const { data: annoStats } = useAnnotationStats(
    selectedProjectId || '',
    project?.datasetid || '',
    aalDatasetSources,
    {
      skip: !selectedProjectId || !project?.datasetid || aalDatasetSources.length === 0,
    }
  )

  // Determine if we should show completed batch results or new AAL interface
  const batchStatus = model?.batch?.status
  const hasCompletedBatch =
    batchStatus === 'COMPLETE' || batchStatus === 'COMPLETE_WITH_ERR' || batchStatus === 'ERR'
  const showBatchResults = hasCompletedBatch && !showingNewAAL

  // Note: Removed auto-initialization to keep Review/Fine-tune phases disabled when configuring new AAL
  // Users must manually select datasets for new AAL runs

  useEffect(() => {
    if (model?.batch?.metadata_filter?.datasource) {
      setAalDatasetSources(model.batch.metadata_filter.datasource)
    }

    setHasUnlabeledImages((annoStats?.statistics.total_unannotated_images || 0) > 0)
  }, [
    aalDatasetSources.length,
    annoStats?.statistics.total_unannotated_images,
    model?.batch?.metadata_filter?.datasource,
  ])

  // Reset showingNewAAL and sync dataset sources when model changes (not when user explicitly starts new AAL)
  useEffect(() => {
    if (selectedModelId && hasCompletedBatch && !showingNewAAL) {
      // Sync dataset sources with the batch configuration to enable Review/Fine-tune phases
      const batchDatasets = model?.batch?.metadata_filter?.datasource
      if (batchDatasets && batchDatasets.length > 0) {
        // Only update if current selection is different from batch configuration
        if (!isEqual(aalDatasetSources, batchDatasets)) {
          setAalDatasetSources(batchDatasets)
        }
      }
    }
  }, [
    selectedModelId,
    hasCompletedBatch,
    showingNewAAL,
    model?.batch?.metadata_filter?.datasource,
    aalDatasetSources,
    dispatch,
  ])

  // Reset showingNewAAL when model changes (separate effect to avoid conflicts)
  useEffect(() => {
    if (selectedModelId && hasCompletedBatch) {
      dispatch(setShowingNewAAL(false))
    }
  }, [dispatch, hasCompletedBatch, selectedModelId]) // Only trigger when selectedModelId changes

  // Sync local state with model status
  useEffect(() => {
    const status = model?.batch?.status || 'UNKNOWN'
    const isRunning = status === 'RUNNING'
    if (isRunning !== localRunningState) {
      setLocalRunningState(isRunning)
    }

    // If batch is running, make sure we're not showing new AAL interface
    if (isRunning && showingNewAAL) {
      dispatch(setShowingNewAAL(false))
    }
  }, [model?.batch?.status, localRunningState, showingNewAAL, dispatch])

  // Show error dialog for error states
  useEffect(() => {
    const status = model?.batch?.status
    if (status === 'ERR' || status === 'COMPLETE_WITH_ERR') {
      const errorMsg = model?.batch?.error || 'Unknown error'
      const truncatedError =
        status === 'COMPLETE_WITH_ERR'
          ? `Completed with error: ${errorMsg.slice(0, 50)}${errorMsg.length > 50 ? '...' : ''}`
          : errorMsg
      if (errorMessage !== truncatedError) {
        setErrorMessage(truncatedError)
      }
    }
  }, [model?.batch?.status, model?.batch?.error, errorMessage])

  const handleAcceptSelections = (sources: string[]) => {
    setAalDatasetSources(sources)
    setShowSelectModal(false)
  }

  const handleOpenModal = () => {
    setShowSelectModal(true)
  }

  const handleCloseModal = () => {
    setShowSelectModal(false)
  }

  const handleStartNewAAL = () => {
    dispatch(setShowingNewAAL(true))
    // Clear current selection to start fresh - this will trigger hasDatasetConfigChanged in parent
    setAalDatasetSources([])
  }

  const handleCancelNewAAL = () => {
    dispatch(setShowingNewAAL(false))
    // When canceling, restore the previous batch configuration if it exists
    if (model?.batch?.metadata_filter?.datasource) {
      setAalDatasetSources(model.batch.metadata_filter.datasource)
    } else {
      setAalDatasetSources([])
    }
  }

  // Ensure dataset sources are cleared when starting new AAL to disable Review/Fine-tune phases
  useEffect(() => {
    if (showingNewAAL) {
      // Force clear dataset sources to ensure Review/Fine-tune phases are disabled
      setAalDatasetSources([])
    }
  }, [showingNewAAL, dispatch])

  const handleAALToggle = async () => {
    const status = model?.batch?.status || 'UNKNOWN'
    const isRunning = status === 'RUNNING'

    if (!selectedModelId) {
      setErrorMessage('No model selected')
      setShowError(true)
      return
    }

    if (isRunning) {
      try {
        setLocalRunningState(false)
        await stopBatchInference({ id: selectedModelId }).unwrap()
        dispatch(
          modelsApi.util.invalidateTags([
            { type: apiTags.Model, id: selectedModelId },
            { type: apiTags.ModelList, id: 'LIST' },
          ])
        )
      } catch (error: any) {
        setLocalRunningState(true) // Revert on error
        setErrorMessage(error?.data?.message || 'Failed to stop batch inference')
        setShowError(true)
      }
    } else {
      if (!selectedProjectId || !project?.datasetid) {
        setErrorMessage('Please select a model and dataset before running inference.')
        setShowError(true)
        return
      }

      try {
        setLocalRunningState(true)
        dispatch(setShowingNewAAL(false)) // Hide new AAL interface when starting

        // Determine which datasets to use
        let datasetsToUse = aalDatasetSources

        // If we're rerunning (showBatchResults), use the previous batch configuration
        if (showBatchResults && model?.batch?.metadata_filter?.datasource) {
          datasetsToUse = model.batch.metadata_filter.datasource
          setAalDatasetSources(datasetsToUse)
        }

        dispatch(
          modelsApi.util.invalidateTags([
            { type: apiTags.Model, id: selectedModelId },
            { type: apiTags.Model, id: 'LIST' },
          ])
        )
        await runBatchInference({
          id: selectedModelId,
          project_id: selectedProjectId,
          dataset_id: project.datasetid,
          metadata_filter: { datasource: datasetsToUse },
        }).unwrap()
      } catch (error: any) {
        setLocalRunningState(false) // Revert on error
        setErrorMessage(error?.data?.message || 'Failed to run batch inference')
        setShowError(true)
      }
    }
  }

  const renderStatusContent = () => {
    const status = model?.batch?.status || 'UNKNOWN'

    switch (status) {
      case 'RUNNING':
        return (
          <>
            <FaSpinner className="spinner" />
            <span>
              Running Active Labeling...
              {model?.batch?.total_content &&
                model.batch.completed_content !== undefined &&
                ` (${model.batch.completed_content} / ${model.batch.total_content})`}
            </span>
          </>
        )
      case 'COMPLETE':
        return (
          <>
            <FaCheck />
            <span>Active Labeling Complete</span>
          </>
        )
      case 'COMPLETE_WITH_ERR':
        return (
          <>
            <FaExclamationTriangle />
            <span>
              Active Labeling Complete with Errors
              {model?.batch?.error && (
                <>
                  : {model.batch.error.slice(0, 50)}
                  {model.batch.error.length > 50 ? '...' : ''}
                </>
              )}
            </span>
          </>
        )
      case 'ERR':
        return (
          <>
            <FaExclamationTriangle />
            <span>Active Labeling Error: {model?.batch?.error || 'Unknown error'}</span>
          </>
        )
      case 'INITIALIZED':
        return (
          <>
            <FaSpinner />
            <span>Initializing Active Labeling...</span>
          </>
        )
      default:
        return null
    }
  }

  // Format duration helper
  const formatDuration = (startTime: string, endTime: string) => {
    const start = new Date(startTime)
    const end = new Date(endTime)
    const diffMs = end.getTime() - start.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffSecs = Math.floor((diffMs % 60000) / 1000)

    if (diffMins > 0) {
      return `${diffMins}m ${diffSecs}s`
    }
    return `${diffSecs}s`
  }

  // Check readiness (only relevant when showing new AAL)
  const hasModel = !!model
  const modelDeployed = model?.deployment?.status === 'IN_SERVICE'
  const hasDatasets = aalDatasetSources.length > 0

  const canStart = hasModel && modelDeployed && hasDatasets && hasUnlabeledImages && !isLoading
  const status = model?.batch?.status || 'UNKNOWN'
  const isRunning = localRunningState || status === 'RUNNING'

  // Calculate progress percentage
  const progress =
    model?.batch?.total_content && model.batch.completed_content !== undefined
      ? (model.batch.completed_content / model.batch.total_content) * 100
      : 0

  return (
    <Container>
      <StatusBar>
        <StatusLeft>
          <SelectModelButton
            onClick={() => {
              console.log('[RunPhase] Select model button clicked')
              setShowSelectModelModal(true)
            }}
            title="Select Model"
          >
            <FaEdit size={12} />
            Select Model
          </SelectModelButton>
          <ModelBadge $deployed={modelDeployed}>
            <StatusIcon $deployed={modelDeployed}>
              {modelDeployed ? <FaCheckCircle /> : <FaExclamationCircle />}
            </StatusIcon>
            <ModelName>{model?.name || 'No model selected'}</ModelName>
          </ModelBadge>
        </StatusLeft>

        <QuickStats>
          <StatItem>
            <span className="value">
              {showBatchResults
                ? model?.batch?.total_content || 0
                : aalDatasetSources.length > 0
                ? annoStats?.statistics.total_unannotated_images || 0
                : 0}
            </span>
            <span>images</span>
          </StatItem>
          <StatItem>
            <span className="value">
              {showBatchResults
                ? model?.batch?.metadata_filter?.datasource?.length || 0
                : aalDatasetSources.length}
            </span>
            <span>datasets</span>
          </StatItem>
        </QuickStats>

        {showBatchResults && (
          <NewAALButton onClick={handleStartNewAAL}>
            <FaPlus />
            <span>New AAL</span>
          </NewAALButton>
        )}

        {showingNewAAL && (
          <NewAALButton onClick={handleCancelNewAAL}>
            <FaTimes />
            <span>Cancel</span>
          </NewAALButton>
        )}
      </StatusBar>

      <MainContent>
        <LeftPanel>
          {showBatchResults && model?.batch ? (
            <BatchResultsCard>
              <BatchResultsHeader>
                <BatchResultsTitle>
                  <CardIcon>
                    <FaCheck />
                  </CardIcon>
                  Batch Results
                </BatchResultsTitle>
                <BatchStatusBadge $status={batchStatus || 'UNKNOWN'}>
                  {batchStatus === 'COMPLETE' && <FaCheck />}
                  {batchStatus === 'COMPLETE_WITH_ERR' && <FaExclamationTriangle />}
                  {batchStatus === 'ERR' && <FaExclamationTriangle />}
                  {(batchStatus || 'UNKNOWN').replace(/_/g, ' ')}
                </BatchStatusBadge>
              </BatchResultsHeader>

              <BatchStatsGrid>
                <BatchStatCard>
                  <BatchStatIcon>
                    <FaImages />
                  </BatchStatIcon>
                  <BatchStatValue>{model.batch.total_content || 0}</BatchStatValue>
                  <BatchStatLabel>Total Images</BatchStatLabel>
                </BatchStatCard>

                <BatchStatCard>
                  <BatchStatIcon>
                    <FaCheckCircle />
                  </BatchStatIcon>
                  <BatchStatValue>{model.batch.completed_content || 0}</BatchStatValue>
                  <BatchStatLabel>Processed</BatchStatLabel>
                </BatchStatCard>

                <BatchStatCard>
                  <BatchStatIcon>
                    <FaChartBar />
                  </BatchStatIcon>
                  <BatchStatValue>
                    {model.batch.total_content
                      ? Math.round(
                          ((model.batch.completed_content || 0) / model.batch.total_content) * 100
                        )
                      : 0}
                    %
                  </BatchStatValue>
                  <BatchStatLabel>Success Rate</BatchStatLabel>
                </BatchStatCard>

                {model.batch.threshold && (
                  <BatchStatCard>
                    <BatchStatIcon>
                      <FaRocket />
                    </BatchStatIcon>
                    <BatchStatValue>{model.batch.threshold}</BatchStatValue>
                    <BatchStatLabel>Threshold</BatchStatLabel>
                  </BatchStatCard>
                )}
              </BatchStatsGrid>

              <BatchMetadata>
                <BatchMetadataTitle>
                  <FaDatabase />
                  Datasets Used
                </BatchMetadataTitle>
                <BatchDatasetTags>
                  {model.batch.metadata_filter?.datasource?.map((dataset: string, idx: number) => (
                    <BatchDatasetTag key={idx}>{dataset}</BatchDatasetTag>
                  )) || []}
                </BatchDatasetTags>

                {model.batch.started_at && (
                  <>
                    <BatchMetadataTitle>
                      <FaCalendarAlt />
                      Timeline
                    </BatchMetadataTitle>
                    <BatchTimeline>
                      <BatchTimelineItem>
                        <BatchTimelineLabel>Started</BatchTimelineLabel>
                        <BatchTimelineValue>
                          {new Date(model.batch.started_at).toLocaleString()}
                        </BatchTimelineValue>
                      </BatchTimelineItem>
                      {model.batch.ended_at && (
                        <BatchTimelineItem>
                          <BatchTimelineLabel>Completed</BatchTimelineLabel>
                          <BatchTimelineValue>
                            {new Date(model.batch.ended_at).toLocaleString()}
                          </BatchTimelineValue>
                        </BatchTimelineItem>
                      )}
                      {model.batch.started_at && model.batch.ended_at && (
                        <BatchTimelineItem>
                          <BatchTimelineLabel>Duration</BatchTimelineLabel>
                          <BatchTimelineValue>
                            {formatDuration(model.batch.started_at, model.batch.ended_at)}
                          </BatchTimelineValue>
                        </BatchTimelineItem>
                      )}
                    </BatchTimeline>
                  </>
                )}
              </BatchMetadata>
            </BatchResultsCard>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon>
                    <FaDatabase />
                  </CardIcon>
                  Dataset Selection
                </CardTitle>
                <ActionButton onClick={handleOpenModal}>
                  <FaImages /> Select
                </ActionButton>
              </CardHeader>

              <CardContent>
                {aalDatasetSources.length === 0 ? (
                  <EmptyDatasets>
                    Click "Select" to choose datasets for active labeling. You can select multiple
                    datasets to increase training data variety.
                  </EmptyDatasets>
                ) : (
                  <DatasetGrid>
                    {aalDatasetSources.map((source, index) => (
                      <DatasetCard key={index}>
                        <div className="selected-indicator">
                          <FaCheck />
                        </div>

                        <div className="dataset-icon">
                          <FaDatabase />
                        </div>

                        <DatasetCardHeader>
                          <div className="dataset-name">{source}</div>
                          <div className="dataset-source">Source: {source}</div>
                          <DatasetTypeBadge $type="training">Training</DatasetTypeBadge>
                        </DatasetCardHeader>

                        <DatasetCardBody>
                          <DatasetStatGrid>
                            <DatasetStatItem>
                              <div className="stat-icon">
                                <FaImages />
                              </div>
                              <div className="stat-content">
                                <div className="stat-label">Images</div>
                                <div className="stat-value">
                                  {aalDatasetSources.length > 0
                                    ? (annoStats?.statistics.total_unannotated_images || 0) +
                                      (annoStats?.statistics.total_annotated_images || 0)
                                    : 0}
                                </div>
                              </div>
                            </DatasetStatItem>
                            <DatasetStatItem>
                              <div className="stat-icon">
                                <FaCheckCircle />
                              </div>
                              <div className="stat-content">
                                <div className="stat-label">Annotated</div>
                                <div className="stat-value">
                                  {aalDatasetSources.length > 0
                                    ? annoStats?.statistics.total_annotated_images || 0
                                    : 0}
                                </div>
                              </div>
                            </DatasetStatItem>
                            <DatasetStatItem>
                              <div className="stat-icon">
                                <FaExclamationCircle />
                              </div>
                              <div className="stat-content">
                                <div className="stat-label">Unlabeled</div>
                                <div className="stat-value">
                                  {aalDatasetSources.length > 0
                                    ? annoStats?.statistics.total_unannotated_images || 0
                                    : 0}
                                </div>
                              </div>
                            </DatasetStatItem>
                            <DatasetStatItem>
                              <div className="stat-icon">
                                <FaChartBar />
                              </div>
                              <div className="stat-content">
                                <div className="stat-label">Completeness</div>
                                <div className="stat-value">
                                  {aalDatasetSources.length > 0
                                    ? Math.round(
                                        ((annoStats?.statistics.total_annotated_images || 0) /
                                          ((annoStats?.statistics.total_annotated_images || 0) +
                                            (annoStats?.statistics.total_unannotated_images ||
                                              0))) *
                                          100
                                      )
                                    : 0}
                                  %
                                </div>
                              </div>
                            </DatasetStatItem>
                          </DatasetStatGrid>
                        </DatasetCardBody>
                      </DatasetCard>
                    ))}
                  </DatasetGrid>
                )}
              </CardContent>
            </Card>
          )}
        </LeftPanel>

        <RightPanel>
          <LaunchCard>
            <LaunchHeader>
              <LaunchTitle>
                {showBatchResults ? 'Rerun Active Labeling' : 'Start Active Labeling'}
              </LaunchTitle>
              <LaunchSubtitle>
                {showBatchResults
                  ? 'Run AAL again with the same configuration'
                  : 'Label images for human review'}
              </LaunchSubtitle>
            </LaunchHeader>

            {!showBatchResults && (
              <ReadinessChecks>
                <CheckItem $ready={hasModel}>
                  <CheckIcon $ready={hasModel}>
                    {hasModel ? <FaCheckCircle /> : <FaExclamationCircle />}
                  </CheckIcon>
                  <span>Model Selected</span>
                </CheckItem>

                <CheckItem $ready={modelDeployed}>
                  <CheckIcon $ready={modelDeployed}>
                    {modelDeployed ? <FaCheckCircle /> : <FaExclamationCircle />}
                  </CheckIcon>
                  <span>Model Deployed</span>
                </CheckItem>

                <CheckItem $ready={hasDatasets}>
                  <CheckIcon $ready={hasDatasets}>
                    {hasDatasets ? <FaCheckCircle /> : <FaExclamationCircle />}
                  </CheckIcon>
                  <span>Dataset Selected</span>
                </CheckItem>

                <CheckItem $ready={hasUnlabeledImages}>
                  <CheckIcon $ready={hasUnlabeledImages}>
                    {hasUnlabeledImages ? <FaCheckCircle /> : <FaExclamationCircle />}
                  </CheckIcon>
                  <span>Unlabeled Data</span>
                </CheckItem>
              </ReadinessChecks>
            )}

            <LaunchButton
              $isRunning={isRunning}
              $canStart={
                showBatchResults ? hasModel && modelDeployed && hasUnlabeledImages : canStart
              }
              onClick={handleAALToggle}
              disabled={
                showBatchResults
                  ? !(hasModel && modelDeployed && hasUnlabeledImages) && !isRunning
                  : !canStart && !isRunning
              }
            >
              {isRunning ? (
                <>
                  <FaStop />
                  Stop Labeling
                </>
              ) : showBatchResults ? (
                <>
                  <FaRedo />
                  Rerun AAL
                </>
              ) : (
                <>
                  <FaRocket />
                  Start Labeling
                </>
              )}
            </LaunchButton>

            {(status === 'ERR' || status === 'COMPLETE_WITH_ERR' || status === 'RUNNING') && (
              <StatusContainer $status={status}>{renderStatusContent()}</StatusContainer>
            )}

            {status === 'RUNNING' && <ProgressBar $progress={progress} />}
          </LaunchCard>
        </RightPanel>
      </MainContent>

      <SelectDatasetModal
        canCreate={false}
        singleSelect={false}
        handleAcceptSelections={handleAcceptSelections}
        selectedSources={aalDatasetSources}
        show={showSelectModal}
        onHide={handleCloseModal}
      />

      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={errorMessage}
        title="Run Phase Error"
      />

      <SelectModelTreeModal
        show={showSelectModelModal}
        onHide={() => {
          console.log('[RunPhase] Closing model selector modal')
          setShowSelectModelModal(false)
        }}
        onModelSelect={(model) => {
          console.log('[RunPhase] Model selected:', model.name)
          dispatch(
            setSelectedModelId({
              modelId: model.id,
              projectId: selectedProjectId || '',
            })
          )
          setShowSelectModelModal(false)
        }}
        selectedModelId={selectedModelId || null}
      />
    </Container>
  )
}

export default RunPhase
